import type { ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import type { SearchEntryCategorySB, SearchEntryChampionshipSB, SearchEntrySportSB } from '@mwl/core-lib';
import type { SearchResults, SportUrlParams } from '@/types';
import type { SportSearchTranslationsByVariant, UseMakeSearchResultsVariantResult } from './SportSearchResult.types';
const translations: SportSearchTranslationsByVariant = {
  sports: {
    header: ['sports', 'Sports'],
    goTo: ['go_to_sport', 'Go to sport']
  },
  categories: {
    header: ['categories', 'Categories'],
    goTo: ['go_to_category', 'Go to category']
  },
  championships: {
    header: ['championships', 'Championships'],
    goTo: ['go_to_championship', 'Go to championship'],
    showMore: ['show_more_championships', 'Show more']
  }
};

/**
 * Хук для формирования поисковых результатов по указанному варианту.
 *
 * Возвращает переводы для заголовков и действий по результатам поиска,
 * а также формирует параметры запроса для создания ссылок на страницы с результатами
 * и подмешивает их в изначальные типы (SportResult, CategoryResult, ChampionshipResult).
 *
 * @param {SearchResults} searchResults - Объект с результатами поиска по разным категориям ('sports', 'categories', 'championships').
 * @param {keyof SearchResults} variant - Вариант результата поиска, который нужно использовать. Может быть одним из: 'sports', 'categories', 'championships'.
 * @returns - Объект, содержащий переводы для заголовков и ссылок, а также модифицированные результаты поиска с параметрами запроса для ссылок (SportResult, CategoryResult, ChampionshipResult типов).
 */
export const useMakeSearchResultsVariant = (searchResults: SearchResults, variant: keyof SearchResults): UseMakeSearchResultsVariantResult => {
  const {
    t
  } = useTranslation('sport', {
    keyPrefix: 'search'
  });
  const makeHrefQuery = (searchResult: SearchEntrySportSB | SearchEntryCategorySB | SearchEntryChampionshipSB): SportUrlParams | null => {
    if (variant === 'sports' && 'id' in searchResult) {
      return {
        sp: String(searchResult.id)
      };
    }
    if (variant === 'categories' && 'sportId' in searchResult) {
      return {
        sp: String(searchResult.sportId),
        ct: String(searchResult.id)
      };
    }
    if (variant === 'championships' && 'sportId' in searchResult && 'categoryId' in searchResult) {
      return {
        sp: String(searchResult.sportId),
        ct: String(searchResult.categoryId),
        ch: String(searchResult.id)
      };
    }
    return {};
  };
  return {
    translations: Object.fromEntries(Object.entries(translations[variant]).map(([key, value]) => [key, (t(...((value as [string, string]) ?? [])) as ReactNode)])),
    searchResultsVariant: (searchResults[variant] ?? []).map(searchResult => {
      return {
        ...searchResult,
        hrefQuery: makeHrefQuery(searchResult)
      };
    })
  };
};